import type { FixMessageTag } from "../domain";
import { FixMessageTagKind } from "../domain";
import type {
  DataDictionaryFieldsIndex,
  CustomFieldDef,
} from "./data-dictionary";
import FIX40 from "../thesaurus/thesaurus_FIX_4_0.json";
import FIX41 from "../thesaurus/thesaurus_FIX_4_1.json";
import FIX42 from "../thesaurus/thesaurus_FIX_4_2.json";
import FIX43 from "../thesaurus/thesaurus_FIX_4_3.json";
import FIX44 from "../thesaurus/thesaurus_FIX_4_4.json";
import FIX50 from "../thesaurus/thesaurus_FIX_5_0.json";
import FIX50SP1 from "../thesaurus/thesaurus_FIX_5_0SP1.json";
import FIX50SP2 from "../thesaurus/thesaurus_FIX_5_0SP2.json";
import FIXT11 from "../thesaurus/thesaurus_FIXT_1_1.json";

export interface FixTagEnum {
  value: string;
  symbolicName: string;
  description: string;
}

export interface FixTag {
  tag: string;
  name: string;
  type: string;
  description: string;
  enum?: FixTagEnum[];
}

export const DEFAULT_FIX_VERSION = "FIX.4.4";

export const listProtocols = () => {
  return [
    "FIX.4.0",
    "FIX.4.1",
    "FIX.4.2",
    "FIX.4.3",
    "FIX.4.4",
    "FIX.5.0",
    "FIX.5.0SP1",
    "FIX.5.0SP2",
    "FIXT.1.1",
  ];
};

export const loadDictionary = (version: string): FixTag[] => {
  switch (version) {
    case "FIX.4.0":
      return FIX40;
    case "FIX.4.1":
      return FIX41;
    case "FIX.4.2":
      return FIX42;
    case "FIX.4.3":
      return FIX43;
    case "FIX.4.4":
      return FIX44;
    case "FIX.5.0":
      return FIX50;
    case "FIX.5.0SP1":
      return FIX50SP1;
    case "FIX.5.0SP2":
      return FIX50SP2;
    case "FIXT.1.1":
      return FIXT11;
    default:
      console.warn(`Unknown FIX version: ${version}. Use FIX44 as fallback.`);
      return FIX44;
  }
};

const FIX_TAG_INDEX = (() => {
  const index: Record<string, Record<string, FixTag>> = {};
  for (const protocol of listProtocols()) {
    const protocolIndex: Record<string, FixTag> = {};
    for (const fixTag of loadDictionary(protocol)) {
      protocolIndex[fixTag.tag] = fixTag;
    }
    index[protocol] = protocolIndex;
  }

  return index;
})();

const FIX_NAME_INDEX = (() => {
  const index: Record<string, Record<string, FixTag>> = {};
  for (const protocol of listProtocols()) {
    const protocolIndex: Record<string, FixTag> = {};
    for (const fixTag of loadDictionary(protocol)) {
      protocolIndex[fixTag.name] = fixTag;
    }
    index[protocol] = protocolIndex;
  }

  return index;
})();

export const listAllFixTagsNames = (fields: CustomFieldDef[]): string[] => {
  const tags: string[] = [];
  for (const protocol of listProtocols()) {
    for (const fixTag of loadDictionary(protocol)) {
      tags.push(`${fixTag.name} ${fixTag.tag}`);
    }
  }

  for (const field of fields) {
    tags.push(`${field.tagDescription} ${field.tag}`);
  }

  return [...new Set(tags)];
};

export const resolveTagDescription = (
  tagId: number,
  protocolVersion?: string,
): string => {
  if (!protocolVersion) {
    protocolVersion = DEFAULT_FIX_VERSION;
  }

  const searchTagId = String(tagId);
  return FIX_TAG_INDEX[protocolVersion][searchTagId]?.description || "";
};

export const resolveEnumValue = (
  tagId: number,
  value: string | null,
  protocolVersion?: string,
): string => {
  if (!protocolVersion) {
    protocolVersion = DEFAULT_FIX_VERSION;
  }

  const searchTagId = String(tagId);
  if (
    Object.prototype.hasOwnProperty.call(FIX_TAG_INDEX, protocolVersion) &&
    Object.prototype.hasOwnProperty.call(
      FIX_TAG_INDEX[protocolVersion],
      searchTagId,
    ) &&
    Object.prototype.hasOwnProperty.call(
      FIX_TAG_INDEX[protocolVersion][searchTagId],
      "enum",
    ) &&
    Array.isArray(FIX_TAG_INDEX[protocolVersion][searchTagId]?.enum)
  ) {
    const resolved = FIX_TAG_INDEX[protocolVersion][searchTagId]?.enum?.find(
      (v) => v.value === value,
    )?.symbolicName;
    if (resolved) {
      return resolved;
    }
  }

  return "";
};

/**
 * Lists all enumeration entries for the given FIX tag id.
 * Mirrors guards and default protocol handling of `resolveEnumValue`,
 * but returns the full collection instead of a single match.
 *
 * Returned array items contain the raw `value`, its `symbolicName`, and the
 * optional `description` when available in the dictionary.
 *
 * If the tag has no enum or the tag/protocol is unknown, returns an empty array.
 */
export const listEnumKeyValues = (
  tagId: number,
  protocolVersion?: string,
): { value: string; symbolicName: string; description?: string }[] => {
  if (!protocolVersion) {
    protocolVersion = DEFAULT_FIX_VERSION;
  }

  const searchTagId = String(tagId);
  if (
    Object.prototype.hasOwnProperty.call(FIX_TAG_INDEX, protocolVersion) &&
    Object.prototype.hasOwnProperty.call(
      FIX_TAG_INDEX[protocolVersion],
      searchTagId,
    ) &&
    Array.isArray(FIX_TAG_INDEX[protocolVersion][searchTagId]?.enum)
  ) {
    // Map to a stable plain object shape; clone to avoid accidental mutations
    return (FIX_TAG_INDEX[protocolVersion][searchTagId]?.enum || []).map(
      (e) => ({
        value: e.value,
        symbolicName: e.symbolicName,
        description: e.description,
      }),
    );
  }

  return [];
};

export const resolveTagName = (
  tagId: number,
  protocolVersion?: string,
): string => {
  if (!protocolVersion) {
    protocolVersion = DEFAULT_FIX_VERSION;
  }

  const searchTagId = String(tagId);
  return FIX_TAG_INDEX[protocolVersion][searchTagId]?.name;
};

export const resolveTagId = (
  tagName: string,
  fieldsIndex?: DataDictionaryFieldsIndex | null,
): number => {
  if (fieldsIndex?.labelToTag.has(tagName)) {
    return fieldsIndex.labelToTag.get(tagName) as number;
  }

  for (const protocol of listProtocols()) {
    if (Object.hasOwn(FIX_NAME_INDEX[protocol], tagName)) {
      return Number(FIX_NAME_INDEX[protocol][tagName].tag);
    }
  }

  console.warn(`Unknown tag name: ${tagName}. Use 0 as fallback.`);

  return 0;
};

const SYSTEM_TAGS = new Set([35, 150]);
const HEADER_TAGS = new Set([8, 9, 35, 49, 56, 34, 52, 10]);

export const resolveTagKind = (tagId: number): FixMessageTagKind => {
  if (SYSTEM_TAGS.has(tagId)) {
    return FixMessageTagKind.SYSTEM;
  }
  if (HEADER_TAGS.has(tagId)) {
    return FixMessageTagKind.HEADER;
  }

  return FixMessageTagKind.CUSTOM;
};

const REQUEST_KIND_TAGS = ["D", "E", "6", "R", "AB", "S", "F"];

export const isRequestKindMsg = (msgType: string) => {
  return REQUEST_KIND_TAGS.includes(msgType);
};

export const isRejectMsg = (msgType: string) => {
  const resolvedEnumValue = resolveEnumValue(resolveTagId("MsgType"), msgType);
  return "Reject" === resolvedEnumValue;
};

export const isExecutionReportMsg = (msgType: string) => {
  const resolvedEnumValue = resolveEnumValue(resolveTagId("MsgType"), msgType);
  return "ExecutionReport" === resolvedEnumValue;
};

export const resolveTagValueByNameFromMessage = (
  tagName: string,
  tags: FixMessageTag[],
) => {
  const protocolVersion: string | undefined | null = tags.find(
    (t) => t.tag === resolveTagId("BeginString"),
  )?.value;

  const tagId = resolveTagId(tagName);
  if (!tagId) {
    return null;
  }

  if (!tags) {
    return null;
  }

  for (const tag of tags) {
    if (tag.tag === tagId) {
      const resolvedValue = resolveEnumValue(
        tag.tag,
        tag.value,
        protocolVersion == null ? undefined : protocolVersion,
      );
      if (resolvedValue) {
        return resolvedValue;
      }

      return tag.value;
    }
  }

  return null;
};

// Length-prefixed data fields per FIX spec. Each of these tags carries a value
// that may contain embedded SOH/separator bytes; the preceding length field
// (tag - 1, with one exception) specifies the byte length of the value so that
// the parser can advance past the data without treating embedded SOH as a
// field boundary. Mirrors QuickFIX/J's Message.parseField data-field branch.
const DATA_FIELD_TAGS: ReadonlyMap<number, number> = new Map([
  [91, 90], // SecureData <- SecureDataLen
  [96, 95], // RawData <- RawDataLength
  [213, 212], // XmlData <- XmlDataLen
  [349, 348], // EncodedIssuer <- EncodedIssuerLen
  [351, 350], // EncodedSecurityDesc <- EncodedSecurityDescLen
  [353, 352], // EncodedListExecInst <- EncodedListExecInstLen
  [355, 354], // EncodedText <- EncodedTextLen
  [357, 356], // EncodedSubject <- EncodedSubjectLen
  [359, 358], // EncodedHeadline <- EncodedHeadlineLen
  [361, 360], // EncodedAllocText <- EncodedAllocTextLen
  [363, 362], // EncodedUnderlyingIssuer <- EncodedUnderlyingIssuerLen
  [365, 364], // EncodedUnderlyingSecurityDesc <- EncodedUnderlyingSecurityDescLen
  [368, 367], // EncodedListStatusText <- EncodedListStatusTextLen
  [446, 445], // EncodedPartyID <- EncodedPartyIDLen
  [523, 522], // EncodedAllocText (extension)
  [89, 93], // Signature <- SignatureLength (special: not tag - 1)
]);

export const isDataField = (tagId: number): boolean => {
  return DATA_FIELD_TAGS.has(tagId);
};

export const getDataFieldLengthTag = (tagId: number): number | undefined => {
  return DATA_FIELD_TAGS.get(tagId);
};

export const nextSampleFixMessage = () => {
  return (
    "8=FIX.4.19=6135=A34=149=EXEC52=20121105-23:24:0656=BANZAI98=0108=3010=003\n" +
    "8=FIX.4.19=6135=A34=149=BANZAI52=20121105-23:24:0656=EXEC98=0108=3010=003\n" +
    "8=FIX.4.19=4935=034=249=BANZAI52=20121105-23:24:3756=EXEC10=228\n" +
    "8=FIX.4.19=4935=034=249=EXEC52=20121105-23:24:3756=BANZAI10=228\n" +
    "8=FIX.4.19=10335=D34=349=BANZAI52=20121105-23:24:4256=EXEC11=135215788257721=138=1000040=154=155=MSFT59=010=062\n" +
    "8=FIX.4.19=13935=834=349=EXEC52=20121105-23:24:4256=BANZAI6=011=135215788257714=017=120=031=032=037=138=1000039=054=155=MSFT150=2151=010=059\n" +
    "8=FIX.4.19=15335=834=449=EXEC52=20121105-23:24:4256=BANZAI6=12.311=135215788257714=1000017=220=031=12.332=1000037=238=1000039=254=155=MSFT150=2151=010=230\n" +
    "8=FIX.4.19=10335=D34=449=BANZAI52=20121105-23:24:5556=EXEC11=135215789503221=138=1000040=154=155=ORCL59=010=047\n" +
    "8=FIX.4.19=13935=834=549=EXEC52=20121105-23:24:5556=BANZAI6=011=135215789503214=017=320=031=032=037=338=1000039=054=155=ORCL150=2151=010=049\n" +
    "8=FIX.4.19=15335=834=649=EXEC52=20121105-23:24:5556=BANZAI6=12.311=135215789503214=1000017=420=031=12.332=1000037=438=1000039=254=155=ORCL150=2151=010=220\n" +
    "8=FIX.4.19=10835=D34=549=BANZAI52=20121105-23:25:1256=EXEC11=135215791235721=138=1000040=244=1054=155=SPY59=010=003\n" +
    "8=FIX.4.19=13835=834=749=EXEC52=20121105-23:25:1256=BANZAI6=011=135215791235714=017=520=031=032=037=538=1000039=054=155=SPY150=2151=010=252\n" +
    "8=FIX.4.19=10435=F34=649=BANZAI52=20121105-23:25:1656=EXEC11=135215791643738=1000041=135215791235754=155=SPY10=198\n" +
    "8=FIX.4.19=8235=334=849=EXEC52=20121105-23:25:1656=BANZAI45=658=Unsupported message type10=000\n" +
    "8=FIX.4.19=10435=F34=749=BANZAI52=20121105-23:25:2556=EXEC11=135215792530938=1000041=135215791235754=155=SPY10=197\n" +
    "8=FIX.4.19=8235=334=949=EXEC52=20121105-23:25:2556=BANZAI45=758=Unsupported message type10=002"
  );
};
